import React from 'react';
import '../styles/legal.css';

const Privacy = () => {
  return (
    <div className="legal-page">
      <div className="legal-container">
        <h1 className="legal-title">ADGRO PRIVACY POLICY</h1>

        <div className="legal-meta">
          <p><strong>Effective Date:</strong> January 18, 2026</p>
          <p><strong>Last Updated:</strong> January 18, 2026</p>
        </div>

        <p className="legal-intro">
          Rhombus Labs Inc. ("Rhombus Labs", "we", "us", or "our") is committed to protecting personal information in accordance with Canada's Personal Information Protection and Electronic Documents Act (PIPEDA) and applicable provincial privacy legislation. Adgro is a product developed and operated by Rhombus Labs Inc.
        </p>
        <p>
          This Privacy Policy explains how we collect, use, disclose, and protect personal information in connection with our website and services.
        </p>

        <section className="legal-section">
          <h2>1. SCOPE OF THIS POLICY</h2>
          <p>This Privacy Policy applies to:</p>
          <ul>
            <li>Visitors to the Adgro website</li>
            <li>Retailers, grocery stores, and businesses that subscribe to the Adgro Service ("Customers")</li>
            <li>Individuals who contact us for inquiries or support</li>
            <li>End consumers whose data may be processed through our advertising platform on behalf of our Customers</li>
          </ul>
        </section>

        <section className="legal-section">
          <h2>2. CONSUMER DATA PROCESSED ON BEHALF OF RETAILERS</h2>
          <p>Adgro processes consumer data solely on behalf of our retail Customers and in accordance with their instructions.</p>

          <h3>2.1 Retailer Responsibility</h3>
          <ul>
            <li>Retailers are the custodians and controllers of consumer data collected through their use of the Adgro platform.</li>
            <li>Retailers are responsible for obtaining all necessary consumer consents and providing appropriate privacy notices.</li>
            <li>Retailers determine how consumer information is collected, used, and disclosed through their advertising campaigns.</li>
          </ul>

          <h3>2.2 Adgro's Role</h3>
          <ul>
            <li>Adgro acts as a service provider and data processor.</li>
            <li>We process consumer information only as necessary to provide our Service and in accordance with our Customers' instructions.</li>
            <li>We do not independently use, sell, or disclose consumer information for our own marketing purposes.</li>
          </ul>
        </section>

        <section className="legal-section">
          <h2>3. INFORMATION WE COLLECT</h2>

          <h3>3.1 Information from Website Visitors</h3>
          <ul>
            <li>Contact information (name, email, phone) when you submit inquiries or request a demo</li>
            <li>Technical data (IP address, browser type, device information) collected automatically</li>
            <li>Usage data (pages visited, time spent, referral source)</li>
            <li>Cookie data and tracking information (see our Cookie Policy)</li>
          </ul>

          <h3>3.2 Information from Customers (Retailers)</h3>
          <ul>
            <li>Business contact information (name, email, phone, company name)</li>
            <li>Account credentials and authentication information</li>
            <li>Billing and payment information</li>
            <li>Service usage data and preferences</li>
            <li>Advertising campaign data and performance metrics</li>
            <li>Communications with our support team</li>
          </ul>

          <h3>3.3 Information Processed for Advertising Services</h3>
          <ul>
            <li>Behavioral data and browsing activity for ad targeting (collected on behalf of Customers)</li>
            <li>Ad performance data (impressions, clicks, conversions)</li>
            <li>Audience analytics and demographic information</li>
            <li>Device identifiers and cookie data for tracking and attribution</li>
          </ul>
        </section>

        <section className="legal-section">
          <h2>4. HOW WE USE INFORMATION</h2>
          <p>We use personal information for the following purposes:</p>
          <ul>
            <li>To provide, maintain, and improve the Service</li>
            <li>To deliver targeted advertising services on behalf of our Customers</li>
            <li>To provide analytics and performance reporting</li>
            <li>To process transactions and send related information</li>
            <li>To respond to inquiries and provide customer support</li>
            <li>To send service-related communications (updates, security alerts)</li>
            <li>To maintain security and prevent fraud</li>
            <li>To comply with legal obligations</li>
            <li>To analyze usage patterns and improve our Service (using aggregated, de-identified data)</li>
          </ul>
        </section>

        <section className="legal-section">
          <h2>5. DISCLOSURE OF INFORMATION</h2>
          <p>We do not sell personal information. We may disclose personal information in the following circumstances:</p>
          <ul>
            <li><strong>Service Providers:</strong> We share information with trusted third-party vendors who assist us in providing the Service (e.g., cloud hosting, payment processing, analytics providers, ad networks). These providers are contractually bound to protect personal information.</li>
            <li><strong>Legal Requirements:</strong> We may disclose information when required by law, court order, or governmental authority.</li>
            <li><strong>Business Transfers:</strong> In connection with a merger, acquisition, or sale of assets, personal information may be transferred to the acquiring entity.</li>
            <li><strong>With Consent:</strong> We may disclose information for other purposes with your explicit consent.</li>
          </ul>
        </section>

        <section className="legal-section">
          <h2>6. CROSS-BORDER DATA PROCESSING</h2>
          <p>Personal information may be processed or stored in jurisdictions outside of Canada, including the United States, where our service providers operate.</p>
          <p>When personal information is transferred outside Canada, we ensure that appropriate safeguards are in place, including:</p>
          <ul>
            <li>Contractual obligations requiring service providers to protect personal information</li>
            <li>Technical security measures (encryption, access controls)</li>
            <li>Compliance with applicable data protection requirements</li>
          </ul>
          <p><strong>Note:</strong> Personal information transferred outside Canada may be subject to the laws of the jurisdiction in which it is processed, including lawful access by government authorities.</p>
        </section>

        <section className="legal-section">
          <h2>7. DATA RETENTION</h2>
          <p>We retain personal information only as long as necessary to fulfill the purposes for which it was collected, including:</p>
          <ul>
            <li>For the duration of the customer relationship and any applicable retention period</li>
            <li>As required to comply with legal obligations</li>
            <li>To resolve disputes and enforce our agreements</li>
          </ul>
          <p>When personal information is no longer required, we securely delete or anonymize it.</p>
        </section>

        <section className="legal-section">
          <h2>8. SECURITY SAFEGUARDS</h2>
          <p>We implement administrative, technical, and physical safeguards appropriate to the sensitivity of the information, including:</p>
          <ul>
            <li>Encryption of data in transit and at rest</li>
            <li>Role-based access controls</li>
            <li>Regular security assessments and monitoring</li>
            <li>Employee training on privacy and security</li>
            <li>Incident response procedures</li>
          </ul>
          <p>While we take reasonable precautions, no method of transmission or storage is completely secure. We cannot guarantee absolute security.</p>
        </section>

        <section className="legal-section">
          <h2>9. YOUR PRIVACY RIGHTS</h2>
          <p>Under PIPEDA and applicable provincial legislation, you have the right to:</p>
          <ul>
            <li><strong>Access:</strong> Request access to the personal information we hold about you.</li>
            <li><strong>Correction:</strong> Request correction of inaccurate or incomplete information.</li>
            <li><strong>Withdrawal of Consent:</strong> Withdraw consent to certain processing activities (subject to legal or contractual restrictions).</li>
            <li><strong>Complaint:</strong> File a complaint with the Office of the Privacy Commissioner of Canada if you believe your privacy rights have been violated.</li>
          </ul>
          <p>To exercise these rights, please contact us using the information provided below.</p>
        </section>

        <section className="legal-section">
          <h2>10. COOKIES AND TRACKING</h2>
          <p>Our website and services use cookies, pixels, and similar tracking technologies to enhance your experience, deliver targeted advertising, and collect usage data.</p>
          <p>For detailed information about our use of cookies and how to manage your preferences, please see our <a href="/cookies">Cookie Policy</a>.</p>
        </section>

        <section className="legal-section">
          <h2>11. ONLINE BEHAVIOURAL ADVERTISING</h2>
          <p>Adgro's platform enables targeted advertising based on user behavior, interests, and demographics. This may involve:</p>
          <ul>
            <li>Collecting data about browsing activity across websites</li>
            <li>Creating audience segments based on inferred interests</li>
            <li>Serving personalized advertisements</li>
            <li>Measuring ad performance and attribution</li>
          </ul>
          <p>Our retail Customers are responsible for providing appropriate notices to their end consumers about these practices. Consumers may opt out of targeted advertising through mechanisms described in our Cookie Policy.</p>
        </section>

        <section className="legal-section">
          <h2>12. THIRD-PARTY LINKS</h2>
          <p>Our website may contain links to third-party websites. We are not responsible for the privacy practices of those websites.</p>
        </section>

        <section className="legal-section">
          <h2>13. CHANGES TO THIS POLICY</h2>
          <p>We may update this Privacy Policy from time to time. Material changes will be communicated through our website or by email. Continued use of the Service after changes constitutes acceptance.</p>
        </section>

        <section className="legal-section">
          <h2>14. CONTACT US</h2>
          <p>For privacy inquiries or to exercise your rights, please contact:</p>
          <div className="legal-contact">
            <p><strong>Rhombus Labs Inc.</strong></p>
            <p>Email: <a href="mailto:support@adgro.ca">support@adgro.ca</a></p>
          </div>
          <p>We will respond to your inquiry within 30 days.</p>
        </section>
      </div>
    </div>
  );
};

export default Privacy;
